import React, { useEffect, useRef, useState } from 'react'

import { useFreeBuyBonusStore, useConfigurationStore, useBuyBonusStore } from '@stores'
import { formatMoney } from '@utils/money'
import { buildAtlasImageStyles } from '@utils/buildAtlasImageStyles'
import { Bonus } from 'game-ui-types'
import { useLoadAtlasesWhenGameSettingsReady, useScaleToFit } from '@hooks'
import { useTranslation } from '@amusnet/translations/react'
import { getBuyBonusAtlasPaths } from '@utils/gameSettings'

interface FBBSingleBonusProps {
    updateSelectedBonus: (bonus: Bonus) => void
}

const FBBSingleBonus: React.FC<FBBSingleBonusProps> = ({ updateSelectedBonus }: FBBSingleBonusProps) => {
    const bonuses = useFreeBuyBonusStore((state) => state.bonuses)
    const styles = useFreeBuyBonusStore((state) => state.styles)

    const findElementByImageName = useBuyBonusStore((state) => state.findElementByImageName)
    const atlasMap = useBuyBonusStore((state) => state.atlasMap)
    const cachedImages = useBuyBonusStore((state) => state.cachedImages)
    const loadAtlasesFromPath = useBuyBonusStore((state) => state.loadAtlasesFromPath)

    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const { t } = useTranslation()
    const currency = useConfigurationStore((state) => state.currency)

    const bonusContainerRef = useRef<HTMLDivElement | null>(null)
    const bonusContentRef = useRef<HTMLDivElement | null>(null)

    const [betValue, setBetValue] = useState('')

    useLoadAtlasesWhenGameSettingsReady(getBuyBonusAtlasPaths, loadAtlasesFromPath)

    const image =
        atlasMap &&
        bonuses[0] &&
        bonuses[0].typeConfiguration!.freeBuyBonusConfiguration?.imageName &&
        findElementByImageName(bonuses[0].typeConfiguration!.freeBuyBonusConfiguration!.imageName)

    const imageStyles = {
        ...(image ? buildAtlasImageStyles(image.atlasName, image.value, cachedImages[image.atlasName]) : {}),
        height: image ? `${image.value.height + 38}px` : undefined, // TODO: check with the designers why the height of in the resource is never okay and needs this correction
    }

    useEffect(() => {
        if (bonuses.length === 1) {
            updateSelectedBonus(bonuses[0])
            setBetValue(formatMoney(Number(bonuses[0].betValue), currencyHideCents))
        }
    }, [bonuses])

    const freeBonusNameText = t('gameui.bonusSystem.freeBonusName', {
        bonusName: bonuses[0].typeConfiguration?.freeBuyBonusConfiguration?.label ?? '',
    })

    const scale = useScaleToFit(bonusContainerRef, bonusContentRef, [
        t('gameui.bonusSystem.youAreAwarded'),
        freeBonusNameText,
    ])

    return (
        <div ref={bonusContainerRef} className="flex flex-col items-center justify-between w-[746px]">
            <div className="flex flex-col w-full">
                <div style={imageStyles} />

                <div
                    id="free-buy-bonus-text"
                    style={{ ...styles.bodyBackground }}
                    className="flex flex-col items-center mt-[-39px] pb-4 whitespace-nowrap"
                >
                    <div ref={bonusContentRef} className=" flex flex-col items-center" style={{ zoom: scale }}>
                        <p style={{ color: styles.secondaryTextColor }} className="text-4xl font-bold">
                            {t('gameui.bonusSystem.youAreAwarded')}
                        </p>
                        <p style={{ color: styles.primaryTextColor }} className="text-4xl font-black leading-[2.2]">
                            {freeBonusNameText}
                        </p>
                    </div>
                    <p style={{ color: styles.secondaryTextColor }} className="text-4xl font-bold">
                        x {betValue} {currency}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default FBBSingleBonus
